/* AOCTANT QUICK ADD — SINGLE-WAVE SURFACE ENGINE
   Reproduces the recovered AOCTANT single-wave visual behavior
   while keeping Horizon commerce completely independent.

   No CSS animation.
   No WAAPI.
   No native-state reset.

   Used by collection/search product cards and by the custom AOCTANT Recommendations cards
   (sections/aoctant-product-recommendations.liquid). Cards rendered later call
   window.AoctantCardQuickAddWave.bind(root); binding is idempotent per button, and the file is
   safe to load more than once on a page.
*/
(() => {
  if (window.AoctantCardQuickAddWave) return;

  const SELECTOR =
    '.aoctant-collection-page product-card .quick-add__button, ' +
    '[data-aoctant-product-card] .quick-add__button';

  const desktop = window.matchMedia(
    '(hover: hover) and (pointer: fine) and (min-width: 750px)'
  );

  if (!desktop.matches) {
    window.AoctantCardQuickAddWave = { bind() {} };
    return;
  }

  const reducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const DURATION = 1000;
  const WHITE_START_Y = 78;
  const BLACK_START_Y = 74;

  const clamp01 = (value) =>
    Math.max(0, Math.min(1, value));

  const easing = (() => {
    const x1 = 0.22;
    const y1 = 0.75;
    const x2 = 0.2;
    const y2 = 1;

    const sample = (a1, a2, t) => {
      const c = 3 * a1;
      const b = 3 * (a2 - a1) - c;
      const a = 1 - c - b;
      return ((a * t + b) * t + c) * t;
    };

    const slope = (a1, a2, t) => {
      const c = 3 * a1;
      const b = 3 * (a2 - a1) - c;
      const a = 1 - c - b;
      return 3 * a * t * t + 2 * b * t + c;
    };

    return (x) => {
      x = clamp01(x);
      let t = x;

      for (let i = 0; i < 6; i += 1) {
        const currentX = sample(x1, x2, t) - x;
        const currentSlope = slope(x1, x2, t);

        if (Math.abs(currentSlope) < 0.000001) break;

        t -= currentX / currentSlope;
        t = clamp01(t);
      }

      return sample(y1, y2, t);
    };
  })();

  const bindButton = (button) => {
    if (button.dataset.aoctantSingleWaveBound === 'true') return;

    button.dataset.aoctantSingleWaveBound = 'true';
    button.dataset.aoctantSurface = 'dark';

    /*
      Remove visual descendants from earlier engines only.
      Horizon children are untouched.
    */
    button
      .querySelectorAll(
        '.aoctant-card-surface-fill,' +
        '.aoctant-card-surface-label,' +
        '.aoctant-card-quick-add-wave,' +
        '.aoctant-card-quick-add-wave__label'
      )
      .forEach((element) => element.remove());

    const wave = document.createElement('span');
    wave.className = 'aoctant-card-quick-add-wave';
    wave.setAttribute('aria-hidden', 'true');

    const label = document.createElement('span');
    label.className = 'aoctant-card-quick-add-wave__label';
    label.setAttribute('aria-hidden', 'true');

    const idleText = Array.from(button.childNodes)
      .filter((node) => node.nodeType === Node.TEXT_NODE)
      .map((node) => node.textContent)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    label.textContent = idleText;

    button.append(wave, label);

    let pointerInside = false;
    let desiredLight = false;

    let committedSurface = 'dark';

    /*
      mode:
      null    = no wave visible
      white   = white wave over dark base
      black   = black wave over light base
    */
    let mode = null;
    let progress = 0;

    let animation = null;
    let frame = 0;

    const setSurface = (surface) => {
      committedSurface = surface;
      button.dataset.aoctantSurface = surface;
    };

    const setMode = (nextMode) => {
      mode = nextMode;

      if (mode === 'white') {
        wave.style.background = '#ffffff';
        label.style.color = '#262323';
      } else if (mode === 'black') {
        wave.style.background = '#262323';
        label.style.color = '#ffffff';
      }
    };

    const render = () => {
      const startY =
        mode === 'black'
          ? BLACK_START_Y
          : WHITE_START_Y;

      const waveY =
        startY * (1 - progress);

      wave.style.transform =
        `translate3d(0,${waveY}%,0)`;

      const labelY =
        178 - (128 * progress);

      label.style.clipPath =
        `ellipse(75% 100% at 50% ${labelY}%)`;

      /*
        Real Horizon checkmark stays above this wave.
        Its color follows whichever surface is visually
        covering the center of the button.
      */
      let nativeColor =
        committedSurface === 'light'
          ? '#262323'
          : '#ffffff';

      if (mode && progress >= 0.5) {
        nativeColor =
          mode === 'white'
            ? '#262323'
            : '#ffffff';
      }

      button.style.setProperty(
        '--aoctant-card-native-color',
        nativeColor
      );
    };

    const currentValue = (now) => {
      if (!animation) return progress;

      if (animation.duration <= 0) {
        return animation.to;
      }

      const raw =
        (now - animation.startedAt) /
        animation.duration;

      const eased = easing(clamp01(raw));

      return (
        animation.from +
        ((animation.to - animation.from) * eased)
      );
    };

    const freeze = (now) => {
      if (!animation) return;

      progress = currentValue(now);
      animation = null;
    };

    const animateTo = (target, now) => {
      const distance =
        Math.abs(target - progress);

      if (distance < 0.0001) {
        progress = target;
        animation = null;
        render();
        return;
      }

      animation = {
        from: progress,
        to: target,
        startedAt: now,
        duration: DURATION * distance,
      };

      if (!frame) {
        frame = requestAnimationFrame(tick);
      }
    };

    const parkWave = () => {
      progress = 0;
      animation = null;
      mode = null;

      wave.style.background = '#ffffff';
      label.style.color = '#262323';

      render();
    };

    const commitLight = () => {
      setSurface('light');
      parkWave();
    };

    const commitDark = () => {
      setSurface('dark');
      parkWave();
    };

    function tick(now) {
      frame = 0;

      if (!animation) {
        render();
        return;
      }

      progress = currentValue(now);

      const finished =
        now >=
        animation.startedAt +
          animation.duration;

      if (finished) {
        progress = animation.to;
        const target = animation.to;
        animation = null;

        render();

        if (target >= 0.999) {
          if (mode === 'white') {
            commitLight();
          } else if (mode === 'black') {
            commitDark();
          }
        } else if (target <= 0.001) {
          parkWave();
        }
      } else {
        render();
      }

      if (animation && !frame) {
        frame = requestAnimationFrame(tick);
      }
    }

    const jumpToDesiredSurface = () => {
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }

      animation = null;
      progress = 0;
      mode = null;

      if (desiredLight) {
        commitLight();
      } else {
        commitDark();
      }
    };

    const reconcile = () => {
      const active =
        !button.disabled &&
        (
          pointerInside ||
          button.matches(':focus-visible')
        );

      if (active === desiredLight) return;

      desiredLight = active;

      if (reducedMotion.matches) {
        jumpToDesiredSurface();
        return;
      }

      const now = performance.now();

      /*
        RECOVERED AOCTANT MOTION POLICY

        Never reverse the existing wave.

        ENTER:
        dark base + fresh WHITE wave from bottom.

        LEAVE:
        white base + fresh BLACK wave from bottom.

        Every interaction starts a new sweep.
      */
      if (desiredLight) {

        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }

        animation = null;

        setSurface('dark');
        setMode('white');
        progress = 0;

        render();
        animateTo(1, now);

      } else {

        if (frame) {
          cancelAnimationFrame(frame);
          frame = 0;
        }

        animation = null;

        setSurface('light');
        setMode('black');
        progress = 0;

        render();
        animateTo(1, now);
      }
    };
    button.addEventListener('mouseenter', () => {
      pointerInside = true;
      reconcile();
    });

    button.addEventListener('mouseleave', () => {
      pointerInside = false;
      reconcile();
    });

    button.addEventListener('focus', reconcile);
    button.addEventListener('blur', reconcile);

    render();
  };

  const bind = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach(bindButton);
  };

  window.AoctantCardQuickAddWave = { bind };
  bind();
})();

/* AOCTANT QUICK ADD INTERACTION HOLD
   Keep collection Quick Add visible through the real Horizon cart lifecycle,
   the native data-added visual cycle, then a 2s post-cycle grace period. */
(() => {
  if (window.AoctantCardQuickAddHold) return;
  window.AoctantCardQuickAddHold = true;

  const media = window.matchMedia(
    '(hover: hover) and (pointer: fine) and (min-width: 750px)'
  );

  if (!media.matches) return;

  const POST_CYCLE_HOLD_MS = 2000;
  const VISUAL_CYCLE_FAILSAFE_MS = 3000;
  const states = new WeakMap();

  const waitForNativeVisualCycle = (button) =>
    new Promise((resolve) => {
      let finished = false;
      let sawAdded = button.dataset.added === 'true';

      const finish = () => {
        if (finished) return;
        finished = true;
        observer.disconnect();
        clearTimeout(detectionTimer);
        clearTimeout(failsafeTimer);
        resolve();
      };

      const observer = new MutationObserver(() => {
        if (button.dataset.added === 'true') {
          sawAdded = true;
          return;
        }

        if (sawAdded) finish();
      });

      observer.observe(button, {
        attributes: true,
        attributeFilter: ['data-added'],
      });

      // If this particular operation never uses data-added,
      // don't hold forever waiting for a visual state that does not exist.
      const detectionTimer = setTimeout(() => {
        if (!sawAdded) finish();
      }, 150);

      const failsafeTimer = setTimeout(
        finish,
        VISUAL_CYCLE_FAILSAFE_MS
      );
    });

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add') return;

    const source =
      event.target instanceof Element ? event.target : null;

    const card = source?.closest('product-card, [data-aoctant-product-card]');
    const button = card?.querySelector('.quick-add__button');

    if (!(button instanceof HTMLElement)) return;

    const previous = states.get(button);

    if (previous?.releaseTimer) {
      clearTimeout(previous.releaseTimer);
    }

    const generation = (previous?.generation || 0) + 1;

    states.set(button, {
      generation,
      releaseTimer: null,
    });

    button.dataset.aoctantCardHold = 'true';

    const visualCycle = waitForNativeVisualCycle(button);
    const cartCycle = Promise.resolve(event.promise);

    Promise.allSettled([cartCycle, visualCycle]).then(() => {
      const current = states.get(button);

      if (!current || current.generation !== generation) return;

      current.releaseTimer = setTimeout(() => {
        const latest = states.get(button);

        if (!latest || latest.generation !== generation) return;

        button.removeAttribute('data-aoctant-card-hold');
        states.delete(button);
      }, POST_CYCLE_HOLD_MS);
    });
  });
})();
